import { useState, useEffect, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  detectSmartPOSProvider, 
  canUseDeepLinks, 
  type TerminalProvider 
} from '@/lib/paymentLauncher';

export interface SmartPOSState {
  /** Whether the device is mobile */
  isMobile: boolean;
  /** Whether the device is a known Smart POS terminal */
  isSmartPOS: boolean;
  /** Detected terminal provider (if any) */
  detectedProvider: TerminalProvider | null;
  /** Whether Deep Links can be used */
  canUseDeepLink: boolean;
  /** User Agent string for debugging */
  userAgent: string;
}

/**
 * Hook to detect Smart POS devices and Deep Link capabilities
 */
export function useSmartPOS(): SmartPOSState {
  const isMobile = useIsMobile();
  const [isSmartPOS, setIsSmartPOS] = useState(false);
  const [detectedProvider, setDetectedProvider] = useState<TerminalProvider | null>(null);
  const [userAgent, setUserAgent] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = navigator.userAgent;
    setUserAgent(ua);

    // Detect Smart POS from User-Agent
    const smartPOSPatterns = [
      /sunmi/i,
      /pax/i,
      /ingenico/i,
      /verifone/i,
      /stone/i,
      /point/i,
      /pointpro/i,
      /moderninha/i,
      /plugpag/i,
      /cielo/i,
      /lio/i,
    ];

    const isDetectedSmartPOS = smartPOSPatterns.some(pattern => pattern.test(ua));
    setIsSmartPOS(isDetectedSmartPOS);

    // Detect specific provider
    const provider = detectSmartPOSProvider();
    setDetectedProvider(provider);
  }, []);

  const canUseDeepLink = useMemo(() => {
    // Can use Deep Links if:
    // 1. Is a Smart POS (always enabled)
    // 2. Is a mobile device (Android/iOS)
    return isSmartPOS || canUseDeepLinks();
  }, [isSmartPOS]);

  return {
    isMobile: !!isMobile,
    isSmartPOS,
    detectedProvider,
    canUseDeepLink,
    userAgent,
  };
}

/**
 * Check if the URL has payment callback parameters
 */
export function hasPaymentCallbackParams(): boolean {
  if (typeof window === 'undefined') return false;
  
  const params = new URLSearchParams(window.location.search);
  return params.has('transaction_id') || params.has('transactionId');
}

/**
 * Parse payment callback parameters from URL
 */
export interface PaymentCallbackParams {
  transactionId: string;
  status: 'success' | 'failed' | 'cancelled' | 'timeout';
  paymentId?: string;
  authorizationCode?: string;
  nsu?: string;
  cardBrand?: string;
  cardLastDigits?: string;
  errorMessage?: string;
}

export function parsePaymentCallbackParams(): PaymentCallbackParams | null {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  
  const transactionId = params.get('transaction_id') || params.get('transactionId');
  if (!transactionId) return null;
  
  const statusParam = params.get('status')?.toLowerCase();
  let status: PaymentCallbackParams['status'] = 'failed';
  
  if (statusParam === 'success' || statusParam === 'approved' || statusParam === '1') {
    status = 'success';
  } else if (statusParam === 'cancelled' || statusParam === 'canceled') {
    status = 'cancelled';
  } else if (statusParam === 'timeout') {
    status = 'timeout';
  }
  
  return {
    transactionId,
    status,
    paymentId: params.get('payment_id') || params.get('paymentId') || undefined,
    authorizationCode: params.get('authorization_code') || params.get('authorizationCode') || undefined,
    nsu: params.get('nsu') || undefined,
    cardBrand: params.get('card_brand') || params.get('cardBrand') || params.get('brand') || undefined,
    cardLastDigits: params.get('card_last_digits') || params.get('lastDigits') || undefined,
    errorMessage: params.get('error_message') || params.get('errorMessage') || undefined,
  };
}
